import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  CssBaseline,
  Collapse,
  Fade,
  ThemeProvider,
  createTheme,
  useMediaQuery,
  type CollapseProps,
  type FadeProps,
  type Theme,
} from '@mui/material';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { isTearsheetThemeDark, TEARSHEET_THEMES, type TearsheetThemeId } from './tearsheetThemes';
import { HFAlertProvider } from '../atoms/HFAlert';

interface HfThemeContextValue {
  mode: HfThemeMode;
  setMode: (mode: HfThemeMode) => void;
  toggleMode: () => void;
}

/**
 * Brand tokens sourced from https://hedgefacts.com Elementor kit + computed styles.
 * Navy-dominant system with a single high-chroma action blue. No orange.
 */
export const hf = {
  navy900: '#050C31',
  navy800: '#0C101A',
  navy700: '#0F1729',
  navy600: '#0E1B60',
  navy500: '#152681',
  navy400: '#0A2A8D',
  slate600: '#324A6D',
  blue500: '#0134D1',
  blue400: '#4870E9',
  blue300: '#6B93F5',
  cyan300: '#53C5E7',
  gray700: '#51545B',
  gray400: '#9AA3B2',
  gray300: '#CFCFCF',
  coolGray200: '#C8D5DC',
  border200: '#E6E6E6',
  bgAlt: '#F3F3F3',
  bgSection: '#F7F7F7',
  bgDefault: '#FAFBFC',
  bgWash: '#E8EEF8',
  white: '#FFFFFF',
  paperDark: '#111A33',
  danger: '#B91C1C',
  /** Caution / missing (gold, not MUI orange-yellow). */
  caution: '#B8953A',
  cautionDark: '#D4BC6A',
  cautionBright: '#E8D59A',
  cautionDeep: '#8A6C24',
  /** Financial gain (MVP tearsheet pos); prefer `tokens.positive` in UI. */
  gain: '#0b8a42',
  gainDark: '#3DDC84',
  /** Financial loss (MVP tearsheet neg); prefer `tokens.negative` in UI. */
  loss: '#c62828',
  lossDark: '#EF5350',
  textDisabledDark: '#8FA3C4',
} as const;

/** Convert `#RRGGBB` (or `#RGB`) + alpha (0–1) to `rgba(...)`. */
export function hfAlpha(hex: string, alpha: number): string {
  const raw = hex.replace('#', '');
  const full =
    raw.length === 3
      ? raw
          .split('')
          .map((c) => `${c}${c}`)
          .join('')
      : raw;
  const n = Number.parseInt(full, 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgba(${r},${g},${b},${alpha})`;
}

export const brandGradients = {
  hero: `linear-gradient(137deg, ${hf.navy800} 0%, ${hf.navy400} 100%)`,
  cta: `linear-gradient(154deg, ${hf.navy900} 0%, ${hf.blue500} 100%)`,
  stats: `linear-gradient(180deg, ${hf.navy900} 0%, ${hf.navy500} 100%)`,
  appLight: `linear-gradient(137deg, ${hf.bgDefault} 0%, ${hf.bgWash} 100%)`,
  appDark: `linear-gradient(137deg, ${hf.navy800} 0%, ${hf.navy400} 100%)`,
} as const;

/** Official LogoGlobe-blue mark — bottom-left → top-right (matches hedgefacts.com). */
export const brandSphereGradient = {
  from: hf.blue500,
  to: hf.cyan300,
} as const;

/** Login / decorative sphere fills by theme mode. */
export const brandSphereGradientByMode = {
  dark: brandSphereGradient,
  light: { from: hf.gray700, to: hf.coolGray200 },
} as const;

export type HfThemeMode = 'dark' | 'light';

export interface HfColorTokens {
  bg: string;
  surface: string;
  card: string;
  text: string;
  muted: string;
  accent: string;
  accentHover: string;
  border: string;
  brandBlue: string;
  appGradient: string;
  /** Positive returns / gains (green). */
  positive: string;
  /** Negative returns / losses (red). */
  negative: string;
}

export const fontFamily = "'Manrope', system-ui, -apple-system, sans-serif";
const FONT_FAMILY = fontFamily;
const COOKIE_NAME = 'hf_theme_mode';
const COOKIE_MAX_AGE = 31536000;
const LEGACY_STORAGE_KEY = 'hf_theme';
const FONT_LINK_ID = 'hf-manrope-font';
const FONT_HREF = 'https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&display=swap';

export function tokensForMode(mode: HfThemeMode): HfColorTokens {
  if (mode === 'dark') {
    return {
      bg: hf.navy800,
      surface: hf.paperDark,
      card: hf.paperDark,
      text: hf.white,
      muted: hf.gray300,
      accent: hf.blue400,
      accentHover: hf.blue300,
      border: hfAlpha(hf.white, 0.15),
      brandBlue: hf.blue400,
      appGradient: brandGradients.appDark,
      positive: hf.gainDark,
      negative: hf.lossDark,
    };
  }

  return {
    bg: hf.bgDefault,
    surface: hf.white,
    card: hf.white,
    text: hf.navy700,
    muted: hf.gray700,
    accent: hf.blue500,
    accentHover: hf.blue400,
    border: hf.border200,
    brandBlue: hf.blue500,
    appGradient: brandGradients.appLight,
    positive: hf.gain,
    negative: hf.loss,
  };
}

function cookieAttributes(): string {
  const secure = typeof window !== 'undefined' && window.location.protocol === 'https:' ? '; Secure' : '';
  return `path=/; SameSite=Strict; max-age=${COOKIE_MAX_AGE}${secure}`;
}

function getThemeCookie(): HfThemeMode | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${COOKIE_NAME}=([^;]*)`));
  const value = match?.[1];
  return value === 'light' || value === 'dark' ? value : null;
}

function setThemeCookie(mode: HfThemeMode): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${COOKIE_NAME}=${mode}; ${cookieAttributes()}`;
}

export function createHfTheme(mode: HfThemeMode): Theme {
  const tokens = tokensForMode(mode);
  const isDark = mode === 'dark';
  // Light needs a real gray: `tokens.border` is the same value as paper borders and
  // vanishes on white. Published as CSS variables so component-level scrollers
  // (HF_SCROLLBAR_SX, DataGrid overlay bars) resolve to the same colors.
  const scrollbar = isDark
    ? { thumb: tokens.border, thumbHover: hf.textDisabledDark, track: hfAlpha(tokens.text, 0.08) }
    : { thumb: hf.gray400, thumbHover: hf.gray700, track: hf.bgAlt };

  return createTheme({
    palette: {
      mode,
      background: {
        default: tokens.bg,
        paper: tokens.surface,
      },
      text: {
        primary: tokens.text,
        secondary: tokens.muted,
        disabled: isDark ? hf.textDisabledDark : hf.gray400,
      },
      primary: {
        main: isDark ? hf.blue400 : hf.blue500,
        light: isDark ? hf.blue300 : hf.blue400,
        dark: isDark ? hf.blue500 : hf.navy400,
        contrastText: hf.white,
      },
      secondary: {
        main: hf.navy500,
        light: hf.slate600,
        dark: hf.navy900,
        contrastText: hf.white,
      },
      info: isDark
        ? {
            main: '#1E5F73',
            light: hf.cyan300,
            dark: '#0F3A48',
            contrastText: hf.white,
          }
        : {
            main: hf.cyan300,
            light: '#7AD6EE',
            dark: '#1A7A96',
            contrastText: hf.navy900,
          },
      success: {
        main: tokens.positive,
        contrastText: hf.white,
      },
      error: {
        main: tokens.negative,
        contrastText: hf.white,
      },
      warning: isDark
        ? {
            main: hf.cautionDark,
            light: hf.cautionBright,
            dark: hf.caution,
            contrastText: hf.navy900,
          }
        : {
            main: hf.caution,
            light: hf.cautionDark,
            dark: hf.cautionDeep,
            contrastText: hf.white,
          },
      divider: tokens.border,
    },
    typography: {
      fontFamily: FONT_FAMILY,
      button: {
        textTransform: 'none',
        fontWeight: 600,
      },
    },
    shape: {
      borderRadius: 10,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          'h1, h2, h3, h4, h5, h6, p': {
            marginTop: 0,
            marginBottom: 0,
          },
          body: {
            backgroundColor: tokens.bg,
            backgroundImage: tokens.appGradient,
            backgroundAttachment: 'fixed',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
          },
          ':root': {
            // Native email/password controls use `color-scheme` to pick fieldtext; without this, focus paints black on dark.
            colorScheme: isDark ? 'dark' : 'light',
            '--hf-scrollbar-thumb': scrollbar.thumb,
            '--hf-scrollbar-thumb-hover': scrollbar.thumbHover,
            '--hf-scrollbar-track': scrollbar.track,
          },
          '*': {
            scrollbarWidth: 'thin',
            scrollbarColor: 'var(--hf-scrollbar-thumb) var(--hf-scrollbar-track)',
          },
          '*::-webkit-scrollbar': {
            width: 8,
            height: 8,
          },
          '*::-webkit-scrollbar-track': {
            backgroundColor: 'var(--hf-scrollbar-track)',
          },
          '*::-webkit-scrollbar-thumb': {
            backgroundColor: 'var(--hf-scrollbar-thumb)',
            borderRadius: 4,
            border: '2px solid transparent',
            backgroundClip: 'content-box',
          },
          '*::-webkit-scrollbar-thumb:hover': {
            backgroundColor: 'var(--hf-scrollbar-thumb-hover)',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
          },
          outlined: {
            color: tokens.text,
            borderColor: isDark ? hfAlpha(hf.white, 0.55) : tokens.border,
            backgroundColor: isDark ? hfAlpha(hf.white, 0.08) : hf.white,
            '&:hover': {
              borderColor: tokens.accent,
              backgroundColor: hfAlpha(tokens.accent, isDark ? 0.2 : 0.08),
              color: isDark ? hf.white : tokens.accent,
            },
          },
        },
        variants: [
          {
            props: { variant: 'outlined', color: 'error' },
            style: {
              color: tokens.negative,
              borderColor: tokens.negative,
            },
          },
        ],
      },
      MuiChip: {
        styleOverrides: {
          outlined: {
            color: tokens.text,
            borderColor: isDark ? hfAlpha(hf.white, 0.55) : tokens.border,
            backgroundColor: isDark ? hfAlpha(hf.white, 0.1) : hf.white,
          },
        },
        variants: [
          {
            props: { variant: 'outlined', color: 'primary' },
            style: {
              color: isDark ? hf.blue300 : tokens.accent,
              borderColor: isDark ? hf.blue300 : tokens.accent,
              backgroundColor: isDark ? hfAlpha(hf.blue400, 0.22) : hfAlpha(tokens.accent, 0.1),
            },
          },
        ],
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: tokens.muted,
            '&.Mui-focused': {
              color: tokens.accent,
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? hf.paperDark : hf.white,
            color: tokens.text,
          },
          input: {
            color: tokens.text,
            WebkitTextFillColor: tokens.text,
            caretColor: tokens.text,
            '&:focus': {
              color: tokens.text,
              WebkitTextFillColor: tokens.text,
              caretColor: tokens.text,
            },
            '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active': {
              WebkitBoxShadow: `0 0 0 1000px ${isDark ? hf.paperDark : hf.white} inset`,
              WebkitTextFillColor: `${tokens.text} !important`,
              color: `${tokens.text} !important`,
              caretColor: tokens.text,
              borderRadius: 'inherit',
              transition: 'background-color 99999s ease-out 0s',
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderColor: tokens.border,
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          switchBase: {
            // White thumb on a darker track — checked and unchecked.
            color: hf.white,
            '&.Mui-checked': {
              color: hf.white,
              '& + .MuiSwitch-track': {
                backgroundColor: tokens.accent,
                opacity: 1,
                borderColor: 'transparent',
              },
            },
          },
          track: {
            opacity: 1,
            backgroundColor: isDark ? hfAlpha(hf.white, 0.18) : hf.gray700,
            border: isDark ? `1px solid ${hfAlpha(hf.white, 0.35)}` : `1px solid ${hf.gray700}`,
            boxSizing: 'border-box',
          },
          thumb: {
            backgroundColor: hf.white,
            boxShadow: isDark ? `0 0 0 1px ${hfAlpha(hf.navy900, 0.45)}` : `0 1px 2px ${hfAlpha(hf.navy900, 0.35)}`,
          },
        },
      },
    },
  });
}

function loadInitialMode(): HfThemeMode {
  if (typeof window === 'undefined') return 'dark';

  const fromCookie = getThemeCookie();
  if (fromCookie) return fromCookie;

  const legacy = window.localStorage.getItem(LEGACY_STORAGE_KEY);
  if (legacy === 'light' || legacy === 'dark') {
    setThemeCookie(legacy);
    window.localStorage.removeItem(LEGACY_STORAGE_KEY);
    return legacy;
  }

  return 'dark';
}

function ensureBrandFontLoaded(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(FONT_LINK_ID)) return;

  const link = document.createElement('link');
  link.id = FONT_LINK_ID;
  link.rel = 'stylesheet';
  link.href = FONT_HREF;
  document.head.appendChild(link);
}

const HfThemeContext = createContext<HfThemeContextValue | undefined>(undefined);

export type HFThemeProviderProps = {
  children: ReactNode;
  /** Seed mode (e.g. Storybook toolbar). Falls back to cookie / default dark. */
  initialMode?: HfThemeMode;
  /** Controlled mode (e.g. loaded user preferences). Wins over cookie / internal state. */
  mode?: HfThemeMode;
  /** Fires on every consumer-driven change so a parent can persist it. */
  onModeChange?: (mode: HfThemeMode) => void;
  /** Persist mode to the theme cookie. Disable in Storybook. */
  persist?: boolean;
  baseline?: boolean;
};

export function HFThemeProvider({
  children,
  initialMode,
  mode: controlledMode,
  onModeChange,
  persist = true,
  baseline = true,
}: HFThemeProviderProps) {
  const [internalMode, setModeState] = useState<HfThemeMode>(() => initialMode ?? loadInitialMode());
  const mode = controlledMode ?? internalMode;

  useEffect(() => {
    ensureBrandFontLoaded();
  }, []);

  useEffect(() => {
    // Cookie is a local cache of the effective mode (login screen reads it before prefs load).
    if (persist) setThemeCookie(mode);
  }, [mode, persist]);

  const setMode = useCallback(
    (next: HfThemeMode) => {
      setModeState(next);
      onModeChange?.(next);
    },
    [onModeChange],
  );
  const toggleMode = useCallback(() => setMode(mode === 'dark' ? 'light' : 'dark'), [mode, setMode]);

  const theme = useMemo(() => createHfTheme(mode), [mode]);
  const contextValue = useMemo<HfThemeContextValue>(() => ({ mode, setMode, toggleMode }), [mode, setMode, toggleMode]);

  return (
    <HfThemeContext.Provider value={contextValue}>
      <ThemeProvider theme={theme}>
        {baseline ? <CssBaseline /> : null}
        <HFAlertProvider>{children}</HFAlertProvider>
      </ThemeProvider>
    </HfThemeContext.Provider>
  );
}

export function useHfThemeMode(): HfThemeContextValue {
  const ctx = useContext(HfThemeContext);
  if (!ctx) throw new Error('useHfThemeMode must be used within an HFThemeProvider');
  return ctx;
}

/** MUI theme for the active HF mode — apps use this instead of `@mui/material/styles`. */
export function useHfTheme(): Theme {
  return useMuiTheme();
}

export function HFCollapse(props: CollapseProps) {
  return <Collapse {...props} />;
}

export function HFFade(props: FadeProps) {
  return <Fade {...props} />;
}

export function useHFMediaQuery(query: string): boolean {
  return useMediaQuery(query);
}

export {
  isDarkTearsheetSkin,
  isTearsheetThemeDark,
  SKINS,
  TEARSHEET_THEME_OPTIONS,
  TEARSHEET_THEMES,
  tearsheetScrollbarVars,
  type TearsheetSkinChip,
  type TearsheetTheme,
  type TearsheetThemeId,
} from './tearsheetThemes';

/** Nested MUI theme for tearsheet demos. */
export function createTearsheetMuiTheme(themeId: TearsheetThemeId, base: Theme): Theme {
  const skin = TEARSHEET_THEMES[themeId];
  const dark = isTearsheetThemeDark(themeId);

  return createTheme({
    ...base,
    palette: {
      ...base.palette,
      mode: dark ? 'dark' : 'light',
      background: {
        ...base.palette.background,
        default: skin.bg,
        paper: skin.panel,
      },
      text: {
        ...base.palette.text,
        primary: skin.text,
        secondary: skin.muted,
      },
      primary: {
        ...base.palette.primary,
        main: skin.accent,
        light: skin.accent,
        dark: skin.accent,
        contrastText: skin.text,
      },
      divider: skin.border,
      success: base.palette.success,
      error: base.palette.error,
    },
    typography: {
      ...base.typography,
      fontFamily,
    },
    components: {
      ...base.components,
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: skin.panel,
            borderColor: skin.border,
          },
        },
      },
    },
  });
}
